import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { userHasNotesAccess } from '../config/notesAccess';
import {
  listNotebooks, listSections, listPages, listAllPages,
  deletePage, searchPagesWithContext,
} from '../oneNoteApi';
import { deleteNoteLinksByPageId } from '../api/figApi';

const S = {
  root: {
    maxWidth: 640, margin: '0 auto',
    padding: '16px 16px 80px',
    fontFamily: 'inherit',
  },
  heading: {
    fontSize: 22, fontWeight: 700, color: '#222',
    margin: '0 0 16px',
  },
  searchInput: {
    display: 'block', width: '100%', boxSizing: 'border-box',
    fontSize: 15, padding: '10px 14px',
    border: '1px solid #ddd', borderRadius: 8,
    outline: 'none', background: '#fff',
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: '#aaa',
    textTransform: 'uppercase', letterSpacing: 0.6,
    margin: '16px 0 6px',
  },
  row: (indent) => ({
    display: 'flex', alignItems: 'center',
    minHeight: 44, paddingLeft: indent, paddingRight: 8,
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer', userSelect: 'none',
    background: indent === 12 ? '#fff' : indent === 28 ? '#fafafa' : '#fff',
  }),
  chevron: {
    marginRight: 8, fontSize: 10, color: '#bbb',
    width: 14, textAlign: 'center', flexShrink: 0,
  },
  nbName: {
    flex: 1, fontSize: 14, fontWeight: 600, color: '#333',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  secName: {
    flex: 1, fontSize: 13, color: '#555',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  pgName: {
    flex: 1, fontSize: 13, color: '#1a73e8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  delBtn: {
    marginLeft: 8, flexShrink: 0,
    background: 'none', border: 'none',
    color: '#ccc', fontSize: 18,
    cursor: 'pointer', padding: '4px 4px',
    minHeight: 36, lineHeight: 1,
  },
  emptyRow: (indent) => ({
    display: 'flex', alignItems: 'center',
    minHeight: 36, paddingLeft: indent,
    color: '#ccc', fontSize: 12,
    borderBottom: '1px solid #f0f0f0',
  }),
  searchResultRow: {
    display: 'flex', alignItems: 'flex-start',
    padding: '10px 4px', borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 12,
    padding: 24, maxWidth: 360, width: '100%',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
  },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#222', margin: '0 0 10px' },
  modalBody: { fontSize: 13, color: '#555', margin: '0 0 6px', lineHeight: 1.5 },
  modalNote: { fontSize: 11, color: '#aaa', margin: '0 0 18px', lineHeight: 1.4 },
  modalActions: { display: 'flex', gap: 8 },
  modalDeleteBtn: {
    flex: 1, padding: '10px', minHeight: 44,
    background: '#c0392b', color: '#fff',
    border: 'none', borderRadius: 8,
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  modalCancelBtn: {
    padding: '10px 20px', minHeight: 44,
    background: '#f0f0f0', color: '#555',
    border: 'none', borderRadius: 8,
    fontSize: 14, cursor: 'pointer',
  },
};

function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .trim();
}

// Returns the URL to use when opening a OneNote search result.
// Section files (.one in the path) must use the onenote: scheme so the OS
// opens the OneNote app instead of the browser downloading the binary file.
function resolveOneNoteUrl(pg) {
  const clientUrl = pg.clientUrl || pg.links?.oneNoteClientUrl?.href;
  if (clientUrl) return clientUrl;
  const webUrl = pg.webUrl || pg.links?.oneNoteWebUrl?.href;
  if (!webUrl) return null;
  const pathPart = webUrl.split('?')[0];
  if (/\.one(\/|$)/i.test(pathPart)) return 'onenote:' + webUrl;
  return webUrl;
}

export default function NotesDestination() {
  const { instance, accounts } = useMsal();
  const isAllowed = userHasNotesAccess(accounts[0]);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  const [notebooks, setNotebooks]       = useState([]);
  const [loadingNbs, setLoadingNbs]     = useState(true);
  const [expandedNbs, setExpandedNbs]   = useState(new Set());
  const [expandedSecs, setExpandedSecs] = useState(new Set());
  const [sectionsByNb, setSectionsByNb] = useState(new Map());
  const [pagesBySec, setPagesBySec]     = useState(new Map());
  const [loadingNbId, setLoadingNbId]   = useState(null);
  const [loadingSecId, setLoadingSecId] = useState(null);

  const [searchQuery, setSearchQuery]           = useState('');
  const [contentResults, setContentResults]     = useState([]);
  const [isContentSearching, setContentSearching] = useState(false);
  const [contentSearchError, setContentSearchError] = useState(null);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting]       = useState(false);
  const [deleteError, setDeleteError]     = useState(null);
  const [error, setError]                 = useState(null);

  // ── load notebooks on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isAllowed) { setLoadingNbs(false); return; }
    async function load() {
      try {
        const token = await getToken();
        const data  = await listNotebooks(token);
        const nbs   = (data.value || []).sort(
          (a, b) => (b.lastModifiedDateTime || '').localeCompare(a.lastModifiedDateTime || '')
        );
        setNotebooks(nbs);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingNbs(false);
      }
    }
    load();
  }, [isAllowed, getToken]);

  // ── content search ───────────────────────────────────────────────────────────
  const runContentSearch = useCallback(async (q) => {
    setContentSearching(true);
    setContentSearchError(null);
    try {
      const token   = await getToken();
      const results = await searchPagesWithContext(token, q);
      setContentResults(results.map(r => ({
        id:      r.pageId,
        title:   r.title.replace(/\.one$/, '') || '(Untitled)',
        snippet: stripHtml(r.summary),
        webUrl:  r.webUrl,
      })));
    } catch (err) {
      setContentSearchError(err.message);
    } finally {
      setContentSearching(false);
    }
  }, [getToken]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setContentResults([]);
      setContentSearchError(null);
      return;
    }
    const timer = setTimeout(() => runContentSearch(q), 600);
    return () => clearTimeout(timer);
  }, [searchQuery, runContentSearch]);

  // ── client-side title matches ────────────────────────────────────────────────
  const titleMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const results = [];
    for (const nb of notebooks) {
      if (nb.displayName.toLowerCase().includes(q)) {
        results.push({ type: 'notebook', id: nb.id, title: nb.displayName, breadcrumb: '' });
      }
      for (const sec of (sectionsByNb.get(nb.id) || [])) {
        if (sec.displayName.toLowerCase().includes(q)) {
          results.push({ type: 'section', id: sec.id, title: sec.displayName, breadcrumb: nb.displayName });
        }
        for (const pg of (pagesBySec.get(sec.id) || [])) {
          if ((pg.title || '').toLowerCase().includes(q)) {
            results.push({
              type: 'page', id: pg.id,
              title:     pg.title || '(Untitled)',
              breadcrumb: `${nb.displayName} › ${sec.displayName}`,
              clientUrl: pg.links?.oneNoteClientUrl?.href,
              webUrl:    pg.links?.oneNoteWebUrl?.href,
            });
          }
        }
      }
    }
    return results;
  }, [searchQuery, notebooks, sectionsByNb, pagesBySec]);

  // ── tree toggle handlers ─────────────────────────────────────────────────────
  async function toggleNotebook(nbId) {
    const next = new Set(expandedNbs);
    if (next.has(nbId)) { next.delete(nbId); setExpandedNbs(next); return; }
    next.add(nbId);
    setExpandedNbs(next);
    if (sectionsByNb.has(nbId)) return;
    setLoadingNbId(nbId);
    try {
      const token = await getToken();
      const data  = await listSections(token, nbId);
      const secs  = (data.value || []).sort((a, b) => a.displayName.localeCompare(b.displayName));
      setSectionsByNb(prev => new Map(prev).set(nbId, secs));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingNbId(null);
    }
  }

  async function toggleSection(secId) {
    const next = new Set(expandedSecs);
    if (next.has(secId)) { next.delete(secId); setExpandedSecs(next); return; }
    next.add(secId);
    setExpandedSecs(next);
    if (pagesBySec.has(secId)) return;
    setLoadingSecId(secId);
    try {
      const token = await getToken();
      const data  = await listPages(token, secId, 100);
      const pages = (data.value || []).sort(
        (a, b) => (b.lastModifiedDateTime || '').localeCompare(a.lastModifiedDateTime || '')
      );
      setPagesBySec(prev => new Map(prev).set(secId, pages));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSecId(null);
    }
  }

  function openPage(pg) {
    const url = resolveOneNoteUrl(pg);
    if (!url) return;
    if (url.startsWith('onenote:')) {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
  }

  // ── delete confirm helpers ───────────────────────────────────────────────────
  function confirmDeletePage(pg, secId) {
    setDeleteError(null);
    setPendingDelete({ type: 'page', id: pg.id, name: pg.title || '(Untitled)', sectionId: secId });
  }

  function confirmDeleteSection(sec, nbId) {
    setDeleteError(null);
    const pages = pagesBySec.get(sec.id);
    setPendingDelete({
      type: 'section', id: sec.id, name: sec.displayName,
      notebookId: nbId, pageCount: pages?.length,
    });
  }

  function confirmDeleteNotebook(nb) {
    setDeleteError(null);
    setPendingDelete({ type: 'notebook', id: nb.id, name: nb.displayName });
  }

  async function executeDelete() {
    if (!pendingDelete || isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const token = await getToken();
      const { type, id, sectionId, notebookId } = pendingDelete;

      if (type === 'page') {
        await deletePage(token, id);
        await deleteNoteLinksByPageId(token, id);
        setPagesBySec(prev => {
          const m = new Map(prev);
          m.set(sectionId, (m.get(sectionId) || []).filter(p => p.id !== id));
          return m;
        });
      }

      if (type === 'section') {
        let pages = pagesBySec.get(id);
        if (!pages) {
          pages = await listAllPages(token, id);
        }
        for (const pg of pages) {
          await deletePage(token, pg.id);
          await deleteNoteLinksByPageId(token, pg.id);
        }
        setSectionsByNb(prev => {
          const m = new Map(prev);
          m.set(notebookId, (m.get(notebookId) || []).filter(s => s.id !== id));
          return m;
        });
        setExpandedSecs(prev => { const s = new Set(prev); s.delete(id); return s; });
        setPagesBySec(prev => { const m = new Map(prev); m.delete(id); return m; });
      }

      if (type === 'notebook') {
        let secs = sectionsByNb.get(id);
        if (!secs) {
          const data = await listSections(token, id);
          secs = data.value || [];
        }
        for (const sec of secs) {
          let pages = pagesBySec.get(sec.id);
          if (!pages) {
            pages = await listAllPages(token, sec.id);
          }
          for (const pg of pages) {
            await deletePage(token, pg.id);
            await deleteNoteLinksByPageId(token, pg.id);
          }
        }
        setNotebooks(prev => prev.filter(nb => nb.id !== id));
        setSectionsByNb(prev => { const m = new Map(prev); m.delete(id); return m; });
        setExpandedNbs(prev => { const s = new Set(prev); s.delete(id); return s; });
      }

      setPendingDelete(null);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  }

  // ── auth gate ────────────────────────────────────────────────────────────────
  if (!isAllowed) {
    return <div style={{ padding: 24, color: '#999', fontSize: 14 }}>Not authorized.</div>;
  }

  const isSearching = searchQuery.trim().length > 0;

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div style={S.root}>
      <h2 style={S.heading}>Notes</h2>

      {error && (
        <p style={{ color: '#c0392b', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
      )}

      {/* Search bar */}
      <input
        type="text"
        placeholder="Search notes…"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && searchQuery.trim().length >= 2) {
            runContentSearch(searchQuery.trim());
          }
        }}
        style={S.searchInput}
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />

      {/* ── Search results view ── */}
      {isSearching && (
        <div>
          {titleMatches.length > 0 && (
            <div>
              <p style={S.sectionLabel}>Title matches ({titleMatches.length})</p>
              {titleMatches.map((r, i) => (
                <div
                  key={`t-${r.type}-${r.id}-${i}`}
                  style={S.searchResultRow}
                  onClick={() => r.type === 'page' && openPage(r)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 500,
                      color: r.type === 'page' ? '#1a73e8' : '#333',
                      display: 'block',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.title}
                    </span>
                    {r.breadcrumb && (
                      <span style={{ fontSize: 11, color: '#aaa', display: 'block', marginTop: 2 }}>
                        {r.breadcrumb}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {titleMatches.length === 0 && !isContentSearching && contentResults.length === 0 && (
            <p style={{ color: '#bbb', fontSize: 13 }}>No title matches in loaded notebooks/sections.</p>
          )}

          <p style={S.sectionLabel}>
            Content matches
            {isContentSearching ? ' — searching…' : ` (${contentResults.length})`}
          </p>

          {contentSearchError && (
            <p style={{ color: '#c0392b', fontSize: 13, margin: '0 0 8px' }}>{contentSearchError}</p>
          )}

          {!isContentSearching && contentResults.length === 0 && !contentSearchError && (
            <p style={{ color: '#bbb', fontSize: 13 }}>
              {searchQuery.trim().length < 2
                ? 'Type at least 2 characters and press Enter to search content.'
                : 'No content matches. Press Enter to search again.'}
            </p>
          )}

          {contentResults.map(pg => (
            <div
              key={`c-${pg.id}`}
              style={S.searchResultRow}
              onClick={() => openPage(pg)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{
                  fontSize: 13, fontWeight: 500, color: '#1a73e8',
                  display: 'block',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {pg.title}
                </span>
                <span style={{
                  fontSize: 11, color: '#888', display: 'block', marginTop: 2,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {pg.snippet || 'content match'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Browse tree view ── */}
      {!isSearching && (
        <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
          {loadingNbs && (
            <p style={{ padding: 16, color: '#aaa', fontSize: 13, margin: 0 }}>Loading notebooks…</p>
          )}

          {!loadingNbs && notebooks.length === 0 && !error && (
            <p style={{ padding: 16, color: '#bbb', fontSize: 13, margin: 0 }}>No notebooks found.</p>
          )}

          {notebooks.map(nb => (
            <div key={nb.id}>
              {/* Notebook row */}
              <div
                style={S.row(12)}
                onClick={() => toggleNotebook(nb.id)}
              >
                <span style={S.chevron}>
                  {loadingNbId === nb.id ? '…' : expandedNbs.has(nb.id) ? '▼' : '▶'}
                </span>
                <span style={S.nbName}>{nb.displayName}</span>
                <button
                  style={S.delBtn}
                  onClick={e => { e.stopPropagation(); confirmDeleteNotebook(nb); }}
                  title="Delete all pages in this notebook"
                  aria-label={`Delete notebook ${nb.displayName}`}
                >×</button>
              </div>

              {/* Sections */}
              {expandedNbs.has(nb.id) && (
                <div>
                  {loadingNbId === nb.id && (
                    <div style={S.emptyRow(32)}>Loading sections…</div>
                  )}
                  {!loadingNbId && (sectionsByNb.get(nb.id) || []).length === 0 && sectionsByNb.has(nb.id) && (
                    <div style={S.emptyRow(32)}>No sections</div>
                  )}

                  {(sectionsByNb.get(nb.id) || []).map(sec => (
                    <div key={sec.id}>
                      {/* Section row */}
                      <div
                        style={S.row(28)}
                        onClick={() => toggleSection(sec.id)}
                      >
                        <span style={S.chevron}>
                          {loadingSecId === sec.id ? '…' : expandedSecs.has(sec.id) ? '▼' : '▶'}
                        </span>
                        <span style={S.secName}>{sec.displayName}</span>
                        <button
                          style={S.delBtn}
                          onClick={e => { e.stopPropagation(); confirmDeleteSection(sec, nb.id); }}
                          title="Delete all pages in this section"
                          aria-label={`Delete section ${sec.displayName}`}
                        >×</button>
                      </div>

                      {/* Pages */}
                      {expandedSecs.has(sec.id) && (
                        <div>
                          {loadingSecId === sec.id && (
                            <div style={S.emptyRow(48)}>Loading pages…</div>
                          )}
                          {!loadingSecId && (pagesBySec.get(sec.id) || []).length === 0 && pagesBySec.has(sec.id) && (
                            <div style={S.emptyRow(48)}>No pages</div>
                          )}

                          {(pagesBySec.get(sec.id) || []).map(pg => (
                            <div
                              key={pg.id}
                              style={{ ...S.row(44), background: '#fff' }}
                            >
                              <span
                                style={S.pgName}
                                onClick={() => openPage(pg)}
                              >
                                {pg.title || '(Untitled)'}
                              </span>
                              <button
                                style={S.delBtn}
                                onClick={e => { e.stopPropagation(); confirmDeletePage(pg, sec.id); }}
                                title="Delete this page"
                                aria-label={`Delete page ${pg.title}`}
                              >×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Delete confirmation modal ── */}
      {pendingDelete && (
        <div style={S.overlay} onClick={() => !isDeleting && setPendingDelete(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h3 style={S.modalTitle}>
              {pendingDelete.type === 'page'
                ? 'Delete page?'
                : pendingDelete.type === 'section'
                ? 'Delete section pages?'
                : 'Delete notebook pages?'}
            </h3>

            {pendingDelete.type === 'page' && (
              <>
                <p style={S.modalBody}>
                  "{pendingDelete.name}" will be permanently deleted from OneNote.
                </p>
                <p style={S.modalNote}>
                  Any DefineHoly links to this page will also be removed.
                </p>
              </>
            )}

            {pendingDelete.type === 'section' && (
              <>
                <p style={S.modalBody}>
                  {pendingDelete.pageCount != null
                    ? `All ${pendingDelete.pageCount} page${pendingDelete.pageCount !== 1 ? 's' : ''} in "${pendingDelete.name}" will be permanently deleted.`
                    : `All pages in "${pendingDelete.name}" will be permanently deleted.`}
                </p>
                <p style={S.modalNote}>
                  DefineHoly links to those pages will be removed. The empty section container
                  remains in OneNote — delete it there if needed.
                </p>
              </>
            )}

            {pendingDelete.type === 'notebook' && (
              <>
                <p style={S.modalBody}>
                  All pages across all sections of "{pendingDelete.name}" will be permanently deleted.
                </p>
                <p style={S.modalNote}>
                  DefineHoly links to those pages will be removed. The notebook and section
                  containers remain in OneNote — delete them there if needed.
                </p>
              </>
            )}

            {deleteError && (
              <p style={{ color: '#c0392b', fontSize: 12, margin: '0 0 12px' }}>{deleteError}</p>
            )}

            <div style={S.modalActions}>
              <button
                style={{ ...S.modalCancelBtn, flex: 'none' }}
                onClick={() => setPendingDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                style={{
                  ...S.modalDeleteBtn,
                  background: isDeleting ? '#e8a09a' : '#c0392b',
                  cursor: isDeleting ? 'default' : 'pointer',
                }}
                onClick={executeDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
